import { useState } from 'react';

import { ActivityDTO, ActivityType, AssetDTO, InspectionDTO, IssueDTO, IssueSource, RepairDTO, ScheduleFormDTO } from '@gearbelt/global';

import documentsVector from '@/assets/vectors/documents.svg';
import { ListItem } from '@/awa-pages/[workspaceId]/assets/[id]/activity/list-item';
import InspectionDrawer from '@/components/inspections/inspection-drawer';
import IssueDrawer from '@/components/issues/issue-drawer';
import RepairDrawer from '@/components/repairs/repair-drawer';
import ViewScheduleRecordDrawer from '@/components/schedule-forms/view-schedule-record-drawer';

export function AWAActivityList({ activities = [], asset }: { activities: ActivityDTO[]; asset: AssetDTO }) {
  const [isInspectionDrawerOpen, setIsInspectionDrawerOpen] = useState(false);
  const [isScheduleFormDrawerOpen, setIsScheduleFormDrawerOpen] = useState(false);
  const [isRepairDrawerOpen, setIsRepairDrawerOpen] = useState(false);
  const [isIssuesDrawerOpen, setIsIssuesDrawerOpen] = useState(false);
  const [selectedInspection, setSelectedInspection] = useState<InspectionDTO | null>(null);
  const [selectedScheduleForm, setSelectedScheduleForm] = useState<ScheduleFormDTO | null>(null);
  const [selectedRepair, setSelectedRepair] = useState<RepairDTO | null>(null);
  const [selectedIssue, setSelectedIssue] = useState<IssueDTO | null>(null);

  /* No activities yet */
  if (activities.length === 0) {
    return (
      <div className="flex h-full w-full flex-col items-center gap-2 px-2 py-25">
        <img src={documentsVector} alt="No activities yet" className="h-[170px] w-[250px]" />
        <div className="text-default-text text-center text-xl font-medium">No activities yet</div>
        <div className="text-secondary-text text-center text-base font-normal">Tap the green button to get started.</div>
      </div>
    );
  }

  function openActivityDrawer(activity: ActivityDTO) {
    if (activity.activityType === ActivityType.Inspection && activity.inspection) {
      setSelectedInspection(activity.inspection);
      setIsInspectionDrawerOpen(true);
      return;
    }
    if (activity.activityType === ActivityType.ScheduleForm && activity.scheduleForm) {
      setSelectedScheduleForm(activity.scheduleForm);
      setIsScheduleFormDrawerOpen(true);
      return;
    }
    if (activity.activityType === ActivityType.Repair && activity.repair) {
      setSelectedRepair(activity.repair);
      setIsRepairDrawerOpen(true);
      return;
    }
    if (activity.activityType === ActivityType.Issue && activity.issue) {
      setSelectedIssue(activity.issue);
      setIsIssuesDrawerOpen(true);
      return;
    }
  }

  return (
    <>
      <div className="flex h-full w-full flex-col items-center gap-2 p-2">
        {activities?.map((activity: ActivityDTO, index: number) =>
          activity.label ? (
            <ListItem
              key={index}
              formType={activity.activityType}
              itemLabel={activity.label}
              itemCompletedAt={new Date(activity.completedAt)}
              itemContactName={activity.contactName ?? undefined}
              itemSubmittedByName={activity.submittedByName ?? undefined}
              onClick={() => {
                openActivityDrawer(activity);
              }}
            />
          ) : (
            <></>
          ),
        )}
      </div>

      <InspectionDrawer noTrigger inspectionId={selectedInspection?.id} open={isInspectionDrawerOpen} onOpenChange={setIsInspectionDrawerOpen} />

      <ViewScheduleRecordDrawer
        noTrigger
        scheduleFormId={selectedScheduleForm?.id}
        open={isScheduleFormDrawerOpen}
        onOpenChange={setIsScheduleFormDrawerOpen}
      />

      <RepairDrawer noTrigger repairId={selectedRepair?.id} open={isRepairDrawerOpen} onOpenChange={setIsRepairDrawerOpen} />

      <IssueDrawer
        noTrigger
        asset={asset}
        issueId={selectedIssue?.id}
        open={isIssuesDrawerOpen}
        source={IssueSource.Manual}
        onOpenChange={setIsIssuesDrawerOpen}
      />
    </>
  );
}
